import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Crop, SunMedium, RotateCw, Check, X, Undo2, Loader2, FlipHorizontal2, FlipVertical2, Move } from 'lucide-react';

const DEFAULT_ADJUSTMENTS = {
  brightness: 100,
  contrast: 100,
  saturate: 100,
  rotation: 0,
  flipH: false,
  flipV: false,
  skewX: 0,
  skewY: 0,
};

const ManualImageEditor = ({ imageUrl, onApply, onCancel }) => {
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [adjustments, setAdjustments] = useState({ ...DEFAULT_ADJUSTMENTS });
  const [applying, setApplying] = useState(false);
  const [activeTab, setActiveTab] = useState('crop');
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  const filterString = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturate}%)`;
  const transformParts = [
    `rotate(${adjustments.rotation}deg)`,
    `scaleX(${adjustments.flipH ? -1 : 1})`,
    `scaleY(${adjustments.flipV ? -1 : 1})`,
    `skewX(${adjustments.skewX}deg)`,
    `skewY(${adjustments.skewY}deg)`,
  ];
  const transformStyle = { transform: transformParts.join(' ') };

  // Determine if image is same-origin (no crossOrigin needed)
  const isSameOrigin = imageUrl && (imageUrl.startsWith('/') || imageUrl.startsWith(window.location.origin));

  const onImageLoad = useCallback((e) => {
    imgRef.current = e.currentTarget;
  }, []);

  const handleAdjustment = (key, value) => {
    setAdjustments(prev => ({ ...prev, [key]: value }));
  };

  const resetAdjustments = () => {
    setAdjustments({ ...DEFAULT_ADJUSTMENTS });
  };

  const resetCrop = () => {
    setCrop(undefined);
    setCompletedCrop(null);
  };

  const handleApply = async () => {
    if (!imgRef.current) return;
    setApplying(true);

    try {
      const image = imgRef.current;
      const canvas = canvasRef.current || document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const naturalW = image.naturalWidth;
      const naturalH = image.naturalHeight;
      const scaleX = naturalW / image.width;
      const scaleY = naturalH / image.height;

      // Determine crop area
      let sx = 0, sy = 0, sw = naturalW, sh = naturalH;
      if (completedCrop && completedCrop.width > 0 && completedCrop.height > 0) {
        sx = completedCrop.x * scaleX;
        sy = completedCrop.y * scaleY;
        sw = completedCrop.width * scaleX;
        sh = completedCrop.height * scaleY;
      }

      // Handle rotation + skew for canvas dimensions
      const rotation = adjustments.rotation;
      const radians = (rotation * Math.PI) / 180;
      const skewXRad = (adjustments.skewX * Math.PI) / 180;
      const skewYRad = (adjustments.skewY * Math.PI) / 180;
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));

      // Account for skew expanding the bounding box
      const skewExpW = Math.abs(Math.tan(skewXRad)) * sh;
      const skewExpH = Math.abs(Math.tan(skewYRad)) * sw;

      const outW = Math.round(sw * cos + sh * sin + skewExpW);
      const outH = Math.round(sw * sin + sh * cos + skewExpH);

      canvas.width = outW;
      canvas.height = outH;

      // Apply adjustments via filter
      ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturate}%)`;

      // Translate to center, apply transforms, then draw
      ctx.translate(outW / 2, outH / 2);
      ctx.rotate(radians);
      ctx.transform(1, Math.tan(skewYRad), Math.tan(skewXRad), 1, 0, 0);
      ctx.scale(adjustments.flipH ? -1 : 1, adjustments.flipV ? -1 : 1);
      ctx.drawImage(image, sx, sy, sw, sh, -sw / 2, -sh / 2, sw, sh);

      // Export as JPEG
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      await onApply(dataUrl);
    } catch (err) {
      console.error('Failed to apply edits:', err);
    } finally {
      setApplying(false);
    }
  };

  const hasChanges = completedCrop?.width > 0 ||
    adjustments.brightness !== 100 ||
    adjustments.contrast !== 100 ||
    adjustments.saturate !== 100 ||
    adjustments.rotation !== 0 ||
    adjustments.flipH ||
    adjustments.flipV ||
    adjustments.skewX !== 0 ||
    adjustments.skewY !== 0;

  const sliders = [
    { key: 'brightness', label: 'Brightness', min: 20, max: 200, icon: SunMedium },
    { key: 'contrast', label: 'Contrast', min: 20, max: 200, icon: SunMedium },
    { key: 'saturate', label: 'Saturation', min: 0, max: 200, icon: SunMedium },
  ];

  return (
    <div data-testid="manual-image-editor" className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-sans text-xs uppercase tracking-[0.15em] text-muted-foreground">
          Manual Image Editor
        </h3>
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="editor-cancel-btn">
          <X size={14} className="mr-1" /> Close
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="crop" data-testid="crop-tab">
            <Crop size={14} className="mr-1.5" /> Crop
          </TabsTrigger>
          <TabsTrigger value="adjust" data-testid="adjust-tab">
            <SunMedium size={14} className="mr-1.5" /> Adjust
          </TabsTrigger>
        </TabsList>

        <TabsContent value="crop" className="mt-3">
          <div className="bg-muted/30 rounded-lg overflow-hidden flex items-center justify-center"
            style={{ maxHeight: 400 }}>
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              ruleOfThirds
            >
              <img
                ref={imgRef}
                src={imageUrl}
                alt="Edit"
                onLoad={onImageLoad}
                style={{
                  maxHeight: 400,
                  maxWidth: '100%',
                  objectFit: 'contain',
                  filter: filterString,
                  ...transformStyle,
                }}
                crossOrigin={isSameOrigin ? undefined : "anonymous"}
                data-testid="editor-image"
              />
            </ReactCrop>
          </div>
          {completedCrop?.width > 0 && (
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-muted-foreground">
                Crop: {Math.round(completedCrop.width)}x{Math.round(completedCrop.height)}px
              </p>
              <Button variant="ghost" size="sm" onClick={resetCrop} className="text-xs">
                <Undo2 size={12} className="mr-1" /> Reset Crop
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="adjust" className="mt-3 space-y-5">
          {/* Preview with filters */}
          <div className="bg-muted/30 rounded-lg overflow-hidden flex items-center justify-center"
            style={{ maxHeight: 280 }}>
            <img
              src={imageUrl}
              alt="Preview"
              style={{
                maxHeight: 280,
                maxWidth: '100%',
                objectFit: 'contain',
                filter: filterString,
                ...transformStyle,
                transition: 'filter 0.15s, transform 0.15s',
              }}
              crossOrigin={isSameOrigin ? undefined : "anonymous"}
              data-testid="adjust-preview"
            />
          </div>

          {/* Adjustment sliders */}
          {sliders.map(({ key, label, min, max }) => (
            <div key={key} className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-foreground">{label}</label>
                <span className="text-xs font-mono text-muted-foreground w-10 text-right">
                  {adjustments[key]}%
                </span>
              </div>
              <Slider
                value={[adjustments[key]]}
                min={min}
                max={max}
                step={1}
                onValueChange={([v]) => handleAdjustment(key, v)}
                data-testid={`slider-${key}`}
              />
            </div>
          ))}

          {/* Rotation */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                <RotateCw size={12} /> Rotation
              </label>
              <span className="text-xs font-mono text-muted-foreground w-10 text-right">
                {adjustments.rotation}°
              </span>
            </div>
            <Slider
              value={[adjustments.rotation]}
              min={-180}
              max={180}
              step={1}
              onValueChange={([v]) => handleAdjustment('rotation', v)}
              data-testid="slider-rotation"
            />
          </div>

          {/* Quick rotation buttons */}
          <div className="flex gap-2">
            {[-90, -45, 0, 45, 90].map(deg => (
              <Button
                key={deg}
                variant={adjustments.rotation === deg ? 'default' : 'outline'}
                size="sm"
                className="flex-1 text-xs"
                onClick={() => handleAdjustment('rotation', deg)}
                data-testid={`rotate-${deg}`}
              >
                {deg === 0 ? '0°' : `${deg > 0 ? '+' : ''}${deg}°`}
              </Button>
            ))}
          </div>

          {/* Flip controls */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground flex items-center gap-1.5">
              <FlipHorizontal2 size={12} /> Flip
            </label>
            <div className="flex gap-2">
              <Button
                variant={adjustments.flipH ? 'default' : 'outline'}
                size="sm"
                className="flex-1 text-xs"
                onClick={() => handleAdjustment('flipH', !adjustments.flipH)}
                data-testid="flip-horizontal"
              >
                <FlipHorizontal2 size={13} className="mr-1.5" /> Horizontal
              </Button>
              <Button
                variant={adjustments.flipV ? 'default' : 'outline'}
                size="sm"
                className="flex-1 text-xs"
                onClick={() => handleAdjustment('flipV', !adjustments.flipV)}
                data-testid="flip-vertical"
              >
                <FlipVertical2 size={13} className="mr-1.5" /> Vertical
              </Button>
            </div>
          </div>

          {/* Skew X */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                <Move size={12} /> Skew X
              </label>
              <span className="text-xs font-mono text-muted-foreground w-10 text-right">
                {adjustments.skewX}°
              </span>
            </div>
            <Slider
              value={[adjustments.skewX]}
              min={-45}
              max={45}
              step={1}
              onValueChange={([v]) => handleAdjustment('skewX', v)}
              data-testid="slider-skewX"
            />
          </div>

          {/* Skew Y */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-foreground flex items-center gap-1.5">
                <Move size={12} /> Skew Y
              </label>
              <span className="text-xs font-mono text-muted-foreground w-10 text-right">
                {adjustments.skewY}°
              </span>
            </div>
            <Slider
              value={[adjustments.skewY]}
              min={-45}
              max={45}
              step={1}
              onValueChange={([v]) => handleAdjustment('skewY', v)}
              data-testid="slider-skewY"
            />
          </div>

          <Button variant="ghost" size="sm" onClick={resetAdjustments} className="w-full text-xs">
            <Undo2 size={12} className="mr-1.5" /> Reset All Adjustments
          </Button>
        </TabsContent>
      </Tabs>

      {/* Action buttons */}
      <div className="flex gap-2 pt-2 border-t border-border">
        <Button
          onClick={handleApply}
          disabled={applying || !hasChanges}
          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
          data-testid="apply-edits-btn"
        >
          {applying ? (
            <><Loader2 size={14} className="mr-1.5 animate-spin" /> Applying...</>
          ) : (
            <><Check size={14} className="mr-1.5" /> Apply Changes</>
          )}
        </Button>
        <Button
          onClick={onCancel}
          variant="outline"
          className="flex-1 border-red-200 text-red-700 hover:bg-red-50"
          data-testid="discard-edits-btn"
        >
          <X size={14} className="mr-1.5" /> Discard
        </Button>
      </div>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default ManualImageEditor;
